import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MatchCard } from "@/components/game/match-card";
import {
  Trophy,
  Upload,
  X,
  ArrowLeft,
  MessageSquare,
  Edit,
  Users2,
  Eye,
  Gamepad,
  Medal,
  Star,
  Cog,
  ChevronLeft,
  Crown,
  Calendar,
  Clock,
  LifeBuoy,
  Swords,
  UserCircle2,
  Shield,
  ArrowUpRight,
  Activity,
  UserPlus,
  UserCheck,
  LogOut,
  Loader2,
  RefreshCw,
  Search,
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { Link } from "wouter";
import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AchievementsShowcase } from "@/components/profile/AchievementsShowcase";
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import PullToRefresh from "react-simple-pull-to-refresh";
import { AvatarSelect } from "@/components/profile/avatarSelect";
import { BannerSelect } from "@/components/profile/bannerSelect";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { getRequiredXpForLevel } from "@shared/utils/profile-utils";

interface MatchHistoryProps {
  matches: any[];
  limit?: number;
}

function MatchHistory({ matches, limit }: MatchHistoryProps) {
  const [, navigate] = useLocation();
  const [expanded, setExpanded] = useState(false);

  // Display limited matches unless expanded
  const displayMatches = expanded ? matches : matches?.slice(0, limit || 3);

  const handleReplayMatch = async (matchId: number) => {
    try {
      const res = await apiRequest("POST", `/api/game-rooms/${matchId}/replay`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message ?? "Failed to replay match");
      }
      const data = await res.json();
      navigate(`/game/${data.id}`);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message ?? "Failed to replay match",
        variant: "destructive",
      });
    }
  };

  if (!matches?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/50">
        <Swords className="w-12 h-12 opacity-20 mb-4" />
        <p className="text-center">No match history available</p>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-4 pt-2 pb-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ staggerChildren: 0.05 }}
    >
      {displayMatches?.map((match) => (
        <motion.div
          key={match.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          whileHover={{ scale: 1.01 }}
          transition={{ duration: 0.2 }}
        >
          <MatchCard 
            match={match}
            onReplayMatch={handleReplayMatch}
          />
        </motion.div>
      ))}

      {matches.length > (limit || 3) && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full mt-2 border border-white/10 hover:bg-white/5 text-white/70"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "Show Less" : `Show All (${matches.length})`}
        </Button>
      )}
    </motion.div>
  );
}

function MatchHistorySection({ userId }: { userId: number }) {
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [gameTypeFilter, setGameTypeFilter] = useState<string>("all");
  const [resultFilter, setResultFilter] = useState<string>("all");
  const { user } = useAuth();
  
  const { data: history, isLoading } = useQuery({
    queryKey: ["matchHistory", userId],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", `/api/profile/history/${userId}`);
        if (!res.ok) throw new Error("Failed to fetch match history");
        
        const data = await res.json();
        console.log("Match history response:", data); // Debug log
        
        // Handle different response formats
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.history)) return data.history;
        if (data && Array.isArray(data.matches)) return data.matches;
        if (data && typeof data === 'object') return []; // Empty array if unexpected format
        
        return [];
      } catch (err) {
        console.error("Match history error:", err);
        setError(err instanceof Error ? err.message : "Failed to load match history");
        return [];
      }
    },
    retry: 1,
  });

  // Filter matches based on search term and filters
  const filteredMatches = useMemo(() => {
    if (!history || !Array.isArray(history)) return [];
    
    return history.filter(match => {
      // Filter by opponent name or game ID (search term)
      const searchMatches = searchTerm === "" || 
        match.id.toString().includes(searchTerm) ||
        match.player1Info?.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        match.player2Info?.username.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by game type
      const typeMatches = gameTypeFilter === "all" || match.gameType === gameTypeFilter;
      
      // Filter by result (won/lost)
      let resultMatches = true;
      if (resultFilter !== "all" && user) {
        const userIsPlayer1 = match.player1 === user.id;
        const userWon = match.winner === user.id;
        
        if (resultFilter === "won") {
          resultMatches = userWon;
        } else if (resultFilter === "lost") {
          resultMatches = !userWon;
        }
      }
      
      return searchMatches && typeMatches && resultMatches;
    });
  }, [history, searchTerm, gameTypeFilter, resultFilter, user]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/60">
        <div className="h-10 w-10 border-2 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p>Loading match history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-white/50">
        <Swords className="w-10 h-10 opacity-20 mb-4" />
        <p className="text-center">Error loading matches</p>
        <p className="text-xs mt-2 text-white/40">{error}</p>
        <Button 
          variant="outline" 
          size="sm"
          className="mt-4 text-xs border-white/10"
          onClick={() => window.location.reload()}
        >
          <Loader2 className="h-3 w-3 mr-1" />
          Retry
        </Button>
      </div>
    );
  }

  const hasMatches = Array.isArray(filteredMatches) && filteredMatches.length > 0;
  const totalMatches = history?.length || 0;
  
  return (
    <div className="space-y-4">
      {/* Filters section */}
      <div className="flex flex-col space-y-3 mb-4">
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-sm font-medium text-white/70">Filters</h3>
          {totalMatches > 0 && (
            <Badge variant="outline" className="bg-black/40 text-white/70 text-xs">
              {filteredMatches.length} of {totalMatches} matches
            </Badge>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-white/40" />
          <input
            type="text"
            placeholder="Search by opponent or match ID..."
            className="w-full rounded-md bg-black/20 border border-white/10 pl-9 pr-4 py-2 text-sm text-white placeholder:text-white/40"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="flex flex-wrap gap-2">
          <select
            className="rounded-md bg-black/20 border border-white/10 px-3 py-1.5 text-sm text-white"
            value={gameTypeFilter}
            onChange={(e) => setGameTypeFilter(e.target.value)}
          >
            <option value="all">All Match Types</option>
            <option value="ranked">Ranked</option>
            <option value="quick">Quick Match</option>
            <option value="tournament">Tournament</option>
          </select>
          
          <select
            className="rounded-md bg-black/20 border border-white/10 px-3 py-1.5 text-sm text-white"
            value={resultFilter}
            onChange={(e) => setResultFilter(e.target.value)}
            disabled={userId !== user?.id}
          >
            <option value="all">All Results</option>
            <option value="won">Wins</option>
            <option value="lost">Losses</option>
          </select>
        </div>
      </div>
      
      {!hasMatches && (
        <div className="flex flex-col items-center justify-center py-6 text-white/50 border border-white/10 rounded-lg">
          <Swords className="w-10 h-10 opacity-20 mb-2" />
          <p className="text-center">No matches found</p>
          {(searchTerm || gameTypeFilter !== "all" || resultFilter !== "all") && (
            <p className="text-xs mt-2 text-white/40">Try adjusting your filters</p>
          )}
        </div>
      )}
      
      {hasMatches && <MatchHistory matches={filteredMatches} limit={3} />}
    </div>
  );
}

export default function ProfilePage() {
  const { user, logoutMutation, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [location, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [showAvatarSelect, setShowAvatarSelect] = useState(false);
  const [showBannerSelect, setShowBannerSelect] = useState(false);

  // Redirect to auth page if user is not authenticated
  if (!authLoading && !user) {
    navigate("/auth");
    return null;
  }

  // Extract userId from query parameters or use current user's id
  const searchParams = new URLSearchParams(window.location.search);
  const queryId = searchParams.get("id");

  // If a query ID is provided, use it regardless of whether it's the current user or not
  // This ensures we respect the explicit navigation to a specific profile
  const profileId = queryId ? Number(queryId) : user?.id;

  // Fetch profile data
  const { data: profile, isLoading } = useQuery({
    queryKey: ["profile", profileId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/profile/${profileId}`);
      if (!res.ok) throw new Error("Failed to fetch profile");
      return res.json();
    },
    enabled: !!profileId,
  });

  // Fetch XP data separately
  const { data: xpData } = useQuery({
    queryKey: ["profile-xp", profileId],
    queryFn: async () => {
      const res = await fetch(`/api/profile/xp/${profileId}`);
      if (!res.ok) throw new Error("Failed to fetch XP data");
      return res.json();
    },
    enabled: !!profileId,
  });

  // Check if users are already friends
  const { data: friendsList } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/friends");
      return res.json();
    },
    enabled: !!user && profileId !== user?.id,
  });

  // Determine if the profile user is already a friend
  const isFriend = friendsList?.some(
    (friend: any) =>
      (friend.userId === profileId || friend.friendId === profileId) &&
      friend.status === "accepted",
  );

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      type,
    }: {
      file: File;
      type: "avatar" | "banner";
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiRequest(
        "POST",
        `/api/profile/photo/${type}`,
        formData,
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to upload image");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", profileId] });
      queryClient.invalidateQueries({ queryKey: ["profile-xp", profileId] });
      toast({ title: "Success", description: "Image uploaded successfully" });
    },
    onError: (error: Error) => {
      toast({
        title: "Error uploading image",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleImageUpload = (type: "avatar" | "banner") => {
    if (type === "avatar") {
      setShowAvatarSelect(true);
    } else {
      setShowBannerSelect(true);
    }
  };

  const handleRefresh = async () => {
    // Invalidate multiple queries to update profile data, stats, achievements and match history
    await queryClient.invalidateQueries({ queryKey: ["profile", profileId] });
    await queryClient.invalidateQueries({ queryKey: ["profile-xp", profileId] });
    await queryClient.invalidateQueries({ queryKey: ["matchHistory", profileId] });
    await queryClient.invalidateQueries({ queryKey: ["achievements", profileId] });
    await queryClient.invalidateQueries({ queryKey: ["avatar-data", profile?.profile?.avatarId, profile?.profile?.avatarSource] });
    await queryClient.invalidateQueries({ queryKey: ["banner-data", profile?.profile?.bannerId, profile?.profile?.bannerSource] });
    return Promise.resolve();
  };

  // Fetch avatar data if avatarId is present
  const { data: avatarData } = useQuery({
    queryKey: ["avatar-data", profile?.profile?.avatarId, profile?.profile?.avatarSource],
    queryFn: async () => {
      if (!profile?.profile?.avatarId) return null;
      
      // Construct endpoint based on avatarSource
      const source = profile.profile.avatarSource || "default";
      const endpoint = source === "shop" 
        ? `/api/customization/shopItem/${profile.profile.avatarId}`
        : `/api/customization/avatars/${profile.profile.avatarId}`;
        
      const res = await apiRequest("GET", endpoint);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!profile?.profile?.avatarId,
  });

  // Fetch banner data if bannerId is present
  const { data: bannerData } = useQuery({
    queryKey: ["banner-data", profile?.profile?.bannerId, profile?.profile?.bannerSource],
    queryFn: async () => {
      if (!profile?.profile?.bannerId) return null;

      // Construct endpoint based on bannerSource
      const source = profile.profile.bannerSource || "default";
      const endpoint = source === "shop" 
        ? `/api/customization/shopItem/${profile.profile.bannerId}` 
        : `/api/customization/banners/${profile.profile.bannerId}`;

      const res = await apiRequest("GET", endpoint);
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!profile?.profile?.bannerId,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-black/95 to-black/90 flex flex-col items-center justify-center py-24 text-white/60">
        <div className="h-16 w-16 border-3 border-primary border-t-transparent rounded-full animate-spin mb-6" />
        <p className="text-xl">Loading profile...</p>
      </div>
    );
  }

  if (!profile) return null;

  // Animation variants
  const pageVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 },
  };

  // Calculate level and next level progress
  const currentLevel = xpData?.level || profile.profile?.level || 1;
  const currentXP = xpData?.xp || profile.profile?.xp || 0;

  const currentLevelXp = getRequiredXpForLevel(currentLevel);
  const nextLevelXp = getRequiredXpForLevel(currentLevel + 1);

  const neededXPforNextLevel = (nextLevelXp - currentXP);

  const xpForNextLevel = nextLevelXp;
  const nextLevelProgress = xpData?.progress || Math.min(100, Math.floor(((currentXP - currentLevelXp) / xpForNextLevel) * 100));

  return (
    
      <div className="min-h-screen bg-gradient-to-b from-black via-black/95 to-black/90 pb-16">
        {/* Condensed Header navigation */}
        <EnhancedHeader 
          title="Profile" 
          backPath="/"
          coloredText={true}
          accentColor="text-primary"
          rightContent={profileId === user?.id ? (
            <div className="flex gap-1">
                          <CurrencyDisplay variant="icon" size="sm" />
              
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/60 hover:bg-black/40 text-white w-8 h-8"
                onClick={() => navigate("/support")}
              >
                <LifeBuoy className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-black/60 hover:bg-black/40 text-white w-8 h-8"
                onClick={() => navigate("/user-settings")}
              >
                <Cog className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full bg-red-950/60 hover:bg-red-900/60 text-white w-8 h-8"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                {logoutMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <LogOut className="h-4 w-4" />
                )}
              </Button>
            </div>
          ) : null}
        />
<PullToRefresh 
      onRefresh={handleRefresh}
      pullingContent={
        <div className="text-center p-2">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
        </div>
      }
      refreshingContent={
        <div className="text-center p-4">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Refreshing profile...</p>
        </div>
      }
    >

      <div>
        {/* Compact Banner and Avatar Layout */}
        <div className="relative">
          {/* Banner - reduced height */}
          <div
            className={cn(
              "relative h-36 bg-gradient-to-r from-black/60 to-black/30 group overflow-hidden",
              profileId === user?.id ? "cursor-pointer" : "",
            )}
            onClick={() => profileId === user?.id && !uploadMutation.isPending && handleImageUpload("banner")}
          >
            {bannerData?.imageUrl ? (
              <>
                <img
                  src={bannerData?.imageUrl}
                  alt="Banner"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-black/80 to-black/60 text-white">
                {profileId === user?.id && (
                  <span className="text-white/40 flex items-center gap-1 text-sm">
                    {uploadMutation.isPending && uploadMutation.variables?.type === "banner" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      ""
                    )}
                    {uploadMutation.isPending && uploadMutation.variables?.type === "banner" 
                      ? "Uploading..." 
                      : ""
                    }
                  </span>
                )}
              </div>
            )}

            {profileId === user?.id && (
              <div 
                className={cn(
                  "absolute inset-0 flex items-center justify-center",
                  uploadMutation.isPending && uploadMutation.variables?.type === "banner"
                    ? "bg-black/70 opacity-100"
                    : "bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                )}
              >
                {uploadMutation.isPending && uploadMutation.variables?.type === "banner" ? (
                  <div className="flex flex-col items-center gap-1">
                    <Loader2 className="h-6 w-6 text-white animate-spin" />
                    <span className="text-white text-xs">Uploading...</span>
                  </div>
                ) : (
                  <div className="absolute right-0 top-0 p-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="bg-black/60 hover:bg-black/80 border-white/20 text-white rounded-md"
                    >
                      Change Banner
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile Avatar - smaller size */}
          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
            <div className="relative group z-10">
              <div
                className={cn(
                  "h-24 w-24 rounded-full overflow-hidden",
                  "ring-3 ring-black shadow-xl",
                  profileId === user?.id ? "cursor-pointer" : "",
                )}
                onClick={() =>
                  profileId === user?.id && !uploadMutation.isPending && handleImageUpload("avatar")
                }
              >
                {avatarData?.imageUrl ? (
                  <img
                    src={avatarData?.imageUrl}
                    alt="Avatar"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-black">
                    <UserCircle2 className="h-12 w-12 text-primary" />
                  </div>
                )}
                {profileId === user?.id && (
                  <div 
                    className={cn(
                      "absolute inset-0 flex items-center justify-center",
                      uploadMutation.isPending && uploadMutation.variables?.type === "avatar"
                        ? "bg-black/70 opacity-100"
                        : "bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
                    )}
                  >
                    {uploadMutation.isPending && uploadMutation.variables?.type === "avatar" ? (
                      <div className="flex flex-col items-center gap-1">
                        <Loader2 className="h-6 w-6 text-white animate-spin" />
                        <span className="text-white text-xs">Uploading...</span>
                      </div>
                    ) : (
                      <Upload className="h-6 w-6 text-white" />
                    )}
                  </div>
                )}
              </div>

              {/* Level Badge - smaller */}
              <div className="absolute -bottom-1 -right-1 bg-primary text-black font-bold rounded-full h-7 w-7 flex items-center justify-center ring-1 ring-black text-sm">
                {currentLevel}
              </div>
            </div>

            <div className="mt-1 text-center">
              <h1 className="text-xl font-bold text-white">
                {profile.user.username}
                {profile.profile?.status === "premium" && (
                  <Crown className="h-4 w-4 text-yellow-400 inline-block ml-1.5 mb-0.5" />
                )}
              </h1>

              <div className="text-xs text-white/60">
                Joined{" "}
                {new Date(profile.user.createdAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Main content - adjusted padding to account for smaller avatar */}
        <div className="container max-w-6xl mx-auto px-4 pt-16 mt-2">
          {/* Inline controls and actions */}
          <div className="flex justify-between items-center mb-4">
            {profileId === user?.id ? (
              <div className="opacity-0 w-20">
                {/* Empty space for alignment */}
              </div>
            ) : !isFriend ? (
              <Button
                variant="outline"
                size="sm"
                className="border-white/10 hover:bg-white/5 text-white h-7 px-2 text-xs"
                onClick={() =>
                  toast({
                    title: "Coming soon",
                    description:
                      "Friend request functionality is under development",
                  })
                }
              >
                <UserPlus className="h-3 w-3 mr-1" />
                Add Friend
              </Button>
            ) : (
              <Badge
                variant="outline"
                className="border-primary/30 text-primary bg-primary/10 h-5 text-xs"
              >
                <UserCheck className="h-3 w-3 mr-1" />
                Friends
              </Badge>
            )}

            {/* Actions */}
            <div className="flex gap-1">
              {profileId !== user?.id && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/10 bg-white/5 hover:bg-white/10 text-white h-7 px-2 text-xs"
                  onClick={() => navigate(`/messages/conversation/${profileId}`)}
                >
                  <MessageSquare className="h-3 w-3 mr-1" />
                  Message
                </Button>
              )}
            </div>
          </div>

          {/* XP Progress - more compact */}

          {profileId === user?.id && (
                <div className="bg-black/40 backdrop-blur-md rounded-lg border border-white/10 p-3 mb-4">
                <div className="flex justify-between items-center mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-4 w-4 text-primary" />
                    <span className="text-white text-sm font-medium">
                      Level {currentLevel}
                    </span>
                  </div>
                  <span className="text-xs text-white/60">
                    {neededXPforNextLevel} XP to level up
                  </span>
                </div>
                <Progress
                  value={nextLevelProgress}
                  className="h-1.5 bg-white/10"
                  style={
                    {
                      "--progress-bar-color": "var(--primary)",
                    } as React.CSSProperties
                  }
                />
              </div>
              )}          
          

          {/* Tabs - compact styling */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-black/40 border border-white/10 mb-4 rounded-lg p-0.5 h-auto">
              <TabsTrigger
                value="overview"
                className={cn(
                  "rounded-md py-2 text-xs transition-all duration-300",
                  activeTab === "overview"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
                onClick={() => setActiveTab("overview")}
              >
                <Activity className="h-3.5 w-3.5 mr-1.5" />
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="achievements"
                className={cn(
                  "rounded-md py-2 text-xs transition-all duration-300",
                  activeTab === "achievements"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
                onClick={() => setActiveTab("achievements")}
              >
                <Trophy className="h-3.5 w-3.5 mr-1.5" />
                Achievements
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className={cn(
                  "rounded-md py-2 text-xs transition-all duration-300",
                  activeTab === "history"
                    ? "bg-primary/90 text-black data-[state=active]:bg-primary"
                    : "hover:bg-white/5",
                )}
                onClick={() => setActiveTab("history")}
              >
                <Swords className="h-3.5 w-3.5 mr-1.5" />
                Matches
              </TabsTrigger>
            </TabsList>

            {/* Content remains the same */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={pageVariants}
                transition={{ duration: 0.3 }}
              >
                <TabsContent value="overview" className="space-y-6 mt-0">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-white">
                    <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col items-center text-center">
                          <Gamepad className="h-10 w-10 text-primary mb-3" />
                          <div className="text-3xl font-bold mb-1">
                            {profile.stats.gamesPlayed || 0}
                          </div>
                          <div className="text-sm text-white/60">
                            Matches Played
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col items-center text-center">
                          <Medal className="h-10 w-10 text-yellow-500 mb-3" />
                          <div className="text-3xl font-bold mb-1">
                            {profile.stats.gamesWon === 0
                              ? "0"
                              : profile.stats.gamesWon || 0}
                          </div>
                          <div className="text-sm text-white/60">Matches Won</div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col items-center text-center">
                        <Trophy className="h-10 w-10 text-green-500 mb-3" />

                          <div className="text-3xl font-bold mb-1">
                            {profile.stats.winRate ? 
                              typeof profile.stats.winRate === 'string' ? 
                                parseInt(profile.stats.winRate).toString() : 
                                Math.round(Number(profile.stats.winRate)).toString()
                            : 0}%
                          </div>
                          <div className="text-sm text-white/60">Win Rate</div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-black/60 backdrop-blur-md border border-white/10 overflow-hidden">
                      <CardContent className="p-6">
                        <div className="flex flex-col items-center text-center">
                          <Star className="h-10 w-10 text-blue-500 mb-3" />
                          <div className="text-3xl font-bold mb-1">
                            {profile.stats.highestScore || 0}
                          </div>
                          <div className="text-sm text-white/60">
                            Highest Score
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                  </div>
                    <div className="text-sm text-white/60 mt-2 italic text-center">
                      {/* Placeholder for additional content */}
                      Calculated from ranked, quick and tournament matches.
                    </div>
                  

                  {/* Recent Matches */}
                  <div className="bg-black/60 backdrop-blur-md rounded-lg border border-white/10 overflow-hidden">
                    <div className="px-6 py-4 border-b border-white/10 bg-black/40">
                      <h3 className="text-lg font-medium flex items-center gap-2">
                        <Clock className="h-5 w-5 text-primary" />
                        <span>Recent Matches</span>
                      </h3>
                    </div>
                    <div className="p-4">
                      {typeof profileId === "number" && (
                        <MatchHistorySection userId={profileId} />
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="achievements" className="mt-0">
                  {typeof profileId === "number" && (
                    <AchievementsShowcase userId={profileId} />
                  )}
                </TabsContent>

                <TabsContent value="history" className="mt-0">
                  <div className="bg-black/60 backdrop-blur-md rounded-lg border border-white/10 p-6">
                    <div className="mb-6">
                      <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
                        <Swords className="h-5 w-5 text-primary" />
                        <span>All Match History</span>
                      </h3>
                      <p className="text-sm text-white/60">
                        View all your past matches, scores, and outcomes.
                      </p>
                    </div>
                    {typeof profileId === "number" && (
                      <MatchHistorySection userId={profileId} />
                    )}
                  </div>
                </TabsContent>
              </motion.div>
            </AnimatePresence>
          </Tabs>
        </div>

        {/* Avatar/Banner Select Dialogs */}
      <Dialog open={showAvatarSelect} onOpenChange={setShowAvatarSelect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Avatar</DialogTitle>
          </DialogHeader>
          <AvatarSelect onClose={() => setShowAvatarSelect(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={showBannerSelect} onOpenChange={setShowBannerSelect}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Banner</DialogTitle>
          </DialogHeader>
          <BannerSelect onClose={() => setShowBannerSelect(false)} />
        </DialogContent>
      </Dialog>

      </div>

      </PullToRefresh>


      </div>
  );
}
